<script setup lang="ts">
import { computed } from 'vue';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import BaseIcon from '@/components/base/BaseIcon.vue';
import { isRecord, toNullableNumber, toNullableString, toStringArray } from '@/utils/coerce';
import { formatDate } from '@/utils/formatters';
import { getRiskLevelDisplay, toTitleCase } from '@/utils/kyc';
import type { KycCategory } from '@/types/kyc';

interface NegativeMediaRecord {
  url: string;
  ruleId: string;
  levelValue: number | null;
  sourceName: string;
  riskCategories: string[];
  generalCategories: string[];
  publicationDate: string | null;
  publicationDateYearsSince: number | null;
}

interface Props {
  category: KycCategory;
}

const props = defineProps<Props>();

const normalizeRecord = (item: Record<string, unknown>): NegativeMediaRecord => ({
  url: toNullableString(item.url) ?? '',
  ruleId: toNullableString(item.rule_id) ?? '',
  levelValue: toNullableNumber(item.level_value),
  sourceName: toNullableString(item.source_name) ?? '',
  riskCategories: toStringArray(item.risk_categories),
  generalCategories: toStringArray(item.general_categories),
  publicationDate: toNullableString(item.publication_date),
  publicationDateYearsSince: toNullableNumber(item.publication_date_years_since),
});

const records = computed<NegativeMediaRecord[]>(() => {
  const raw = props.category.data_json;
  const mediaItems = isRecord(raw) ? raw.media_items : raw;
  if (!Array.isArray(mediaItems)) return [];

  return mediaItems
    .filter(isRecord)
    .map(normalizeRecord);
});

const singleRecord = computed(() => records.value.length === 1 ? records.value[0] : null);

const getDisplayCategories = (record: NegativeMediaRecord): string[] => {
  if (record.riskCategories.length > 0) return record.riskCategories;
  return record.generalCategories;
};

const formatCategory = (value: string): string => {
  if (value === 'uncategorized') return 'Sem categoria';
  return toTitleCase(value.replace(/_/g, ' '));
};

const isValidUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

const formatPublicationAge = (yearsSince: number | null): string => {
  if (yearsSince === null) return '';
  const rounded = Number(yearsSince.toFixed(1));
  const formatted = rounded.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
  return rounded === 1 ? 'há 1 ano' : `há ${formatted} anos`;
};

const formatRuleId = (ruleId: string): string => ruleId ? ruleId.slice(0, 8) : '';

const getRiskDisplay = (levelValue: number | null) => getRiskLevelDisplay(levelValue);
</script>

<template>
  <div class="mt-3">
    <div v-if="singleRecord" class="overflow-hidden rounded-md border border-slate-200 bg-white">
      <div class="grid grid-cols-1 divide-y divide-slate-100 text-sm md:grid-cols-[1fr_1fr_1.4fr_auto] md:divide-x md:divide-y-0">
        <div class="min-w-0 p-3">
          <div class="mb-1 text-[11px] font-semibold uppercase text-slate-500">Fonte</div>
          <div class="flex min-w-0 items-center gap-2 font-medium text-slate-900">
            <BaseIcon class="shrink-0 text-base text-slate-500">newspaper</BaseIcon>
            <span class="truncate">{{ singleRecord.sourceName || 'Fonte não informada' }}</span>
          </div>
          <div v-if="formatRuleId(singleRecord.ruleId)" class="mt-0.5 text-[11px] text-slate-500">
            Regra <span class="font-mono">{{ formatRuleId(singleRecord.ruleId) }}</span>
          </div>
        </div>

        <div class="p-3">
          <div class="mb-1 text-[11px] font-semibold uppercase text-slate-500">Publicação</div>
          <div class="font-medium text-slate-900">
            {{ formatDate(singleRecord.publicationDate, undefined, 'Sem data') }}
          </div>
          <div v-if="formatPublicationAge(singleRecord.publicationDateYearsSince)" class="text-xs text-slate-500">
            {{ formatPublicationAge(singleRecord.publicationDateYearsSince) }}
          </div>
        </div>

        <div class="min-w-0 p-3">
          <div class="mb-1 text-[11px] font-semibold uppercase text-slate-500">Categorias</div>
          <div v-if="getDisplayCategories(singleRecord).length" class="flex flex-wrap gap-1.5">
            <span
              v-for="categoryName in getDisplayCategories(singleRecord)"
              :key="categoryName"
              class="inline-flex max-w-full items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"
            >
              <span class="truncate">{{ formatCategory(categoryName) }}</span>
            </span>
          </div>
          <span v-else class="text-xs text-slate-500">Sem categorias</span>
        </div>

        <div class="p-3 md:flex md:items-center">
          <a
            v-if="isValidUrl(singleRecord.url)"
            :href="singleRecord.url"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-2.5 py-1.5 text-xs font-semibold text-blue-700 transition hover:border-blue-300 hover:bg-blue-100"
          >
            <BaseIcon class="text-sm">open_in_new</BaseIcon>
            link
          </a>
          <span v-else class="text-xs text-slate-500">Sem link</span>
        </div>
      </div>
    </div>

    <DataTable
      v-else-if="records.length > 1"
      :value="records"
      responsiveLayout="scroll"
      stripedRows
      class="p-datatable-sm text-sm"
    >
      <Column header="Risco">
        <template #body="{ data }: { data: NegativeMediaRecord }">
          <span
            :class="[
              'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold',
              getRiskDisplay(data.levelValue).borderClass,
            ]"
            :style="{
              backgroundColor: getRiskDisplay(data.levelValue).hexBgColor,
              color: getRiskDisplay(data.levelValue).hexColor,
            }"
          >
            <BaseIcon class="text-sm">{{ getRiskDisplay(data.levelValue).icon }}</BaseIcon>
            {{ getRiskDisplay(data.levelValue).label }}
          </span>
        </template>
      </Column>

      <Column header="Fonte">
        <template #body="{ data }: { data: NegativeMediaRecord }">
          <div class="flex min-w-0 items-center gap-2 font-medium text-slate-900">
            <BaseIcon class="shrink-0 text-base text-slate-500">newspaper</BaseIcon>
            <span class="max-w-48 truncate">{{ data.sourceName || 'Fonte não informada' }}</span>
          </div>
          <div v-if="formatRuleId(data.ruleId)" class="mt-0.5 text-[11px] text-slate-500">
            Regra <span class="font-mono">{{ formatRuleId(data.ruleId) }}</span>
          </div>
        </template>
      </Column>

      <Column header="Publicação">
        <template #body="{ data }: { data: NegativeMediaRecord }">
          <div class="font-medium text-slate-900">{{ formatDate(data.publicationDate, undefined, 'Sem data') }}</div>
          <div v-if="formatPublicationAge(data.publicationDateYearsSince)" class="text-xs text-slate-500">
            {{ formatPublicationAge(data.publicationDateYearsSince) }}
          </div>
        </template>
      </Column>

      <Column header="Categorias">
        <template #body="{ data }: { data: NegativeMediaRecord }">
          <div v-if="getDisplayCategories(data).length" class="flex max-w-64 flex-wrap gap-1.5">
            <span
              v-for="categoryName in getDisplayCategories(data)"
              :key="categoryName"
              class="inline-flex max-w-full items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"
            >
              <span class="truncate">{{ formatCategory(categoryName) }}</span>
            </span>
          </div>
          <span v-else class="text-xs text-slate-500">Sem categorias</span>
        </template>
      </Column>

      <Column header="Evidência">
        <template #body="{ data }: { data: NegativeMediaRecord }">
          <a
            v-if="isValidUrl(data.url)"
            :href="data.url"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 hover:text-blue-900 hover:underline"
          >
            <BaseIcon class="text-sm">open_in_new</BaseIcon>
            link
          </a>
          <span v-else class="text-xs text-slate-500">Sem link</span>
        </template>
      </Column>
    </DataTable>

    <div v-else class="text-sm italic text-slate-500">
      Nenhuma mídia negativa encontrada.
    </div>
  </div>
</template>
